import { useState } from 'react';
import { ChevronDown, Menu, X, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Link, useLocation } from '@tanstack/react-router';
import { useUser } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';

// Header Component
const Header = () => {
    const [isOpen, setIsOpen] = useState(false);
    const { isSignedIn } = useUser();
    const location = useLocation();
    const { data: courses = [] } = useQuery({
        queryKey: queryKeys.coursesList({}),
        queryFn: () => api.getCoursesList(),
        enabled: true,
        select: d => d.courses ?? d,
    });

    const [navLinks] = useState({
        Programs: [
            {
                name: 'Software Engineering Core',
                href: 'https://zinduaschool.com/programs/software-engineering-core/',
            },
            {
                name: 'Data Science Core',
                href: 'https://zinduaschool.com/programs/data-science-core/',
            },
            // { TODO: Update the Cybersecurity href when available
            //     name: 'Cybersecurity Core',
            //     href: '',
            // },
            {
                name: 'Data Analytics Focus',
                href: 'https://zinduaschool.com/programs/data-analytics-focus/',
            },
            {
                name: 'Frontend Development Focus',
                href: 'https://zinduaschool.com/programs/frontend-development-focus/',
            },
            {
                name: 'DevOps Engineering Specialization',
                href: 'https://zinduaschool.com/programs/devops-engineering-specialisation/',
            },
            {
                name: 'Data Engineering Specialization',
                href: 'https://zinduaschool.com/programs/data-engineering-specialisation/',
            },
            // { TODO: Update the AI Engineering href when available
            //     name: 'AI Engineering Specialization',
            //     href: '',
            // },
        ],
        Courses: [],
        Resources: [
            {
                name: 'Discord Community',
                href: 'https://discord.gg/mXVPnfEh4t',
            },
            { name: 'GitHub Organization', href: 'http://github.com/zinduaschool' },
            { name: 'Events & Webinars', href: 'https://lu.ma/zinduaschool' },
            { name: 'Paid Masterclasses', href: 'https://zinduaschool.com/masterclasses' },
            {
                name: 'Short Courses/ Challenges/ Courses',
                href: 'https://zinduaschool.com/courses/',
            },
            // { name: "Alumni Portal", href: "https://alumni.zinduaschool.com" },
        ],
    });

    const getDashboardLink = () => {
        const { pathname } = location;
        if (pathname.startsWith('/cover-course/')) {
            // e.g., /cover-course/react-basics
            const slug = pathname.split('/')[2] || '';
            return { to: '/course/$courseSlug', params: { courseSlug: slug } };
        }
        if (pathname === '/available-courses') {
            // e.g., /available-courses
            return { to: '/explore-courses' };
        }
        return { to: '/dashboard' };
    };
    const dashboardLink = getDashboardLink();

    const [expandedSections, setExpandedSections] = useState({});
    const toggleSection = title => setExpandedSections(prev => ({ ...prev, [title]: !prev[title] }));

    return (
        <header className="bg-neutral-50 fixed w-full z-50 mb-20">
            <nav className="px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    <div className="shrink-0">
                        <Link to="/" preload="intent" preloadDelay={0}>
                            <h1 className="text-xl font-bold text-neutral-950">Zindua LMS</h1>
                        </Link>
                    </div>

                    <div className="hidden lg:flex items-center gap-8">
                        {Object.entries(navLinks).map(([title, links]) => (
                            <DropdownMenu key={title}>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center text-neutral-700 hover:text-neutral-900 focus:outline-hidden">
                                        {title} <ChevronDown className="ml-1 h-4 w-4" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="bg-neutral-50 text-neutral-900 border border-neutral-300">
                                    {title === 'Courses'
                                        ? courses
                                              .filter(course => course.price === 0)
                                              .map(course => (
                                                  <DropdownMenuItem key={course.id} className="hover:bg-neutral-500" asChild>
                                                      <Link
                                                          to="/cover-course/$courseSlug"
                                                          params={{ courseSlug: course.slug }}
                                                          preload="intent"
                                                          preloadDelay={0}
                                                      >
                                                          {course.title}
                                                      </Link>
                                                  </DropdownMenuItem>
                                              ))
                                        : links.map(link => (
                                              <DropdownMenuItem key={link.name} className="hover:bg-neutral-500" asChild>
                                                  {link.href.startsWith('http') ? (
                                                      <a href={link.href} target="_blank" rel="noopener noreferrer">
                                                          {link.name}
                                                      </a>
                                                  ) : (
                                                      <Link to={link.href} preload="intent" preloadDelay={0}>
                                                          {link.name}
                                                      </Link>
                                                  )}
                                              </DropdownMenuItem>
                                          ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ))}
                    </div>

                    <div className="hidden lg:flex items-center gap-4">
                        {isSignedIn ? (
                            <Link to={dashboardLink} preload="intent" preloadDelay={0}>
                                <Button variant="destructive" className="flex justify-center items-center gap-2">
                                    Continue to Dashboard <ChevronRight size={16} />
                                </Button>
                            </Link>
                        ) : (
                            <>
                                <Link to="/sign-in" preload="intent" preloadDelay={0}>
                                    <Button variant="outline" className="px-4 py-2 w-32 flex justify-center items-center">
                                        Login
                                    </Button>
                                </Link>
                                <Link to="/sign-up" preload="intent" preloadDelay={0}>
                                    <Button variant="destructive" className="px-4 py-2 w-32 flex justify-center items-center">
                                        Register
                                    </Button>
                                </Link>
                            </>
                        )}
                    </div>

                    <div className="lg:hidden flex items-center">
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="inline-flex items-center justify-center p-2 rounded-md text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100 focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary"
                        >
                            <span className="sr-only">Open main menu</span>
                            {isOpen ? (
                                <X className="block h-6 w-6" aria-hidden="true" />
                            ) : (
                                <Menu className="block h-6 w-6" aria-hidden="true" />
                            )}
                        </button>
                    </div>
                </div>
            </nav>

            {isOpen && (
                <div className="lg:hidden">
                    <div className="px-2 pt-2 pb-3 flex flex-col gap-1 sm:px-3 max-h-[90vh] overflow-y-auto">
                        {Object.entries(navLinks).map(([title, links]) => (
                            <div key={title} className="text-base font-medium">
                                <button
                                    onClick={() => toggleSection(title)}
                                    className="w-full text-left flex items-center justify-between text-neutral-700 px-3 py-2 rounded-md"
                                >
                                    <span>{title}</span>
                                    <ChevronDown
                                        className={`ml-2 h-4 w-4 transition-transform ${
                                            expandedSections[title] ? 'rotate-180' : ''
                                        }`}
                                    />
                                </button>

                                {expandedSections[title] && (
                                    <div>
                                        {title === 'Courses'
                                            ? courses
                                                  .filter(course => course.price === 0)
                                                  .map(course => (
                                                      <Link
                                                          key={course.id}
                                                          to="/cover-course/$courseSlug"
                                                          params={{ courseSlug: course.slug }}
                                                          className="block w-full text-left text-neutral-700 hover:text-neutral-900 pl-8 pr-3 py-2 rounded-md"
                                                      >
                                                          {course.title}
                                                      </Link>
                                                  ))
                                            : links.map(link =>
                                                  link.href.startsWith('http') ? (
                                                      <a
                                                          key={link.name}
                                                          href={link.href}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="block w-full text-left text-neutral-700 hover:text-neutral-900 pl-8 pr-3 py-2 rounded-md"
                                                      >
                                                          {link.name}
                                                      </a>
                                                  ) : (
                                                      <Link
                                                          key={link.name}
                                                          to={link.href}
                                                          className="block w-full text-left text-neutral-700 hover:text-neutral-900 pl-8 pr-3 py-2 rounded-md"
                                                      >
                                                          {link.name}
                                                      </Link>
                                                  )
                                              )}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="mt-3 border-t border-neutral-200 pt-3 px-3">
                            {isSignedIn ? (
                                <Link to={dashboardLink} preload="intent" preloadDelay={0}>
                                    <Button variant="destructive" className="w-full flex justify-center items-center gap-2">
                                        Continue to Dashboard <ChevronRight size={16} />
                                    </Button>
                                </Link>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    <Link to="/sign-in" preload="intent" preloadDelay={0}>
                                        <Button variant="outline" className="w-full">
                                            Login
                                        </Button>
                                    </Link>
                                    <Link to="/sign-up" preload="intent" preloadDelay={0}>
                                        <Button variant="destructive" className="w-full">
                                            Register
                                        </Button>
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
